import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChevronDown,
  FolderKanban,
  Keyboard,
  Moon,
  MousePointerClick,
  PanelLeft,
  Plus,
  Search,
  Sparkles,
  Sun,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { SearchHit } from '@proerp/shared';
import {
  Badge,
  Button,
  cn,
  Command,
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Spinner,
  useSidebar,
} from '@proerp/ui';
import { useOpenTabs } from '@/components/openTabs';
import { QUICK_ACTIONS } from '@/components/quickActions';
import { useNavItems } from '@/components/useNavItems';
import { formatShortcut, keyLabel } from '@/lib/platformKeys';
import { SHORTCUT_KEYS } from '@/lib/shortcuts';
import { useTheme } from '@/lib/theme';
import { useToasts } from '@/lib/toast';
import { usePermissions } from '@/lib/useCurrentUser';
import { useKeyPlatform } from '@/lib/useKeyPlatform';
import { useUiStore } from '@/store/uiStore';
import type { CopyTarget } from './hitAnswer';
import { actionElementId, HitAnswerCard, Kbd } from './HitAnswerCard';
import { featureRoute, useSearch } from './searchApi';
import {
  countHits,
  formatSimilarity,
  RESOURCE_ICON,
  RESOURCE_LABEL,
  searchBadgeLabel,
  splitSearchGroups,
  type SearchResourceTypeUi,
} from './searchMeta';
import { useHitAnswer } from './useHitAnswer';
import { isPlainLeftClick, usePaletteNavigate } from './usePaletteNavigate';

/**
 * Ein Befehl in der Palette (Sprung/Umschalter/Schnellaktion) — kein Suchtreffer.
 * Sprünge tragen ihr Ziel als `to` und werden als echter Anker gerendert
 * (`LinkCommandItem`); nur Umschalter ohne Ziel (Design, Seitenleiste) haben `run`.
 */
type PaletteCommand = {
  id: string;
  label: string;
  hint?: string;
  keywords?: string;
  icon: LucideIcon;
  group: 'Aktionen' | 'Navigation' | 'Ansicht';
} & ({ to: string; run?: never } | { run: () => void; to?: never });

/** cmdk-Werte der Sonderzeilen (kollidieren nie mit `type-id`-Werten). */
const ALL_RESULTS_VALUE = '__all-results__';
const MORE_GROUPS_VALUE = '__more-groups__';
const MORE_SIMILAR_VALUE = '__more-similar__';
const CREATE_CUSTOMER_VALUE = '__create-customer__';
const CREATE_PROJECT_VALUE = '__create-project__';

/** Ab so vielen Zeichen bietet „Nichts gefunden" das Anlegen an — „ab" ist noch kein Name. */
const CREATE_MIN_LENGTH = 3;

/**
 * Unter dieser Breite fehlt die Vorschau-Spalte (`md`) — dann gibt es auch keine
 * Aktionsliste, in die → oder Tab springen könnte.
 */
const NARROW_QUERY = '(max-width: 47.99rem)';

/** „Nichts gefunden — neu anlegen?" (Vorschlag C): Formular mit dem Suchtext als Name. */
interface CreateRow {
  value: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  to: string;
}

/**
 * Obergrenzen der Liste. Die Suche kennt inzwischen rund 30 Ressourcentypen
 * (Tech-Lead 2026-08-12: „wirklich alles durchsuchen") — ohne Deckel stünden bei
 * einem allgemeinen Begriff 30 Überschriften und ein paar hundert Zeilen im
 * Fenster, durch die man sich mit den Pfeiltasten durchhangeln müsste. Deshalb
 * zeigt die Palette erst einen Ausschnitt und klappt auf Wunsch (Zeile am Ende
 * des Abschnitts, mit Enter erreichbar) den Rest auf.
 */
const VISIBLE_CHIP_LIMIT = 10;
const VISIBLE_GROUP_LIMIT = 8;
const VISIBLE_SIMILAR_LIMIT = 6;

/** Zähl-Text mit deutscher Ein-/Mehrzahl („1 Kategorie" / „3 Kategorien"). */
function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** Eindeutiger cmdk-Wert eines Treffers — dient zugleich als Vorschau-Schlüssel. */
function hitValue(hit: SearchHit): string {
  return `${hit.type}-${hit.id}`;
}

/**
 * Palettenzeile mit Ziel als echter Router-Anker (CLAUDE.md §9, Navigations-Audit
 * P2): Rechtsklick → „In neuem Tab öffnen", Cmd-/Mittelklick und die Ziel-URL in
 * der Statusleiste gibt es nur mit `<a href>`. Bis 2026-09-29 sprangen Treffer
 * per `navigate()`.
 *
 * Tastatur bleibt cmdk-Sache: Pfeiltasten markieren, Enter löst `onSelect` aus —
 * das navigiert programmatisch (ein Tastendruck ist kein Klick auf den Anker).
 * `tabIndex={-1}`, damit Tab nicht durch jeden Treffer wandert.
 *
 * Der Mausklick gehört dem Anker. cmdk hängt an der Zeile selbst einen
 * `onClick`, der `onSelect` auslöst — ohne `stopPropagation` liefe der Sprung
 * zweimal (doppelter Verlaufseintrag), und ein Cmd-Klick öffnete den neuen Tab
 * UND spränge im aktuellen. Das ist kein Zeilen-Guard im Sinne von
 * `shouldHandleRowClick`, sondern die Grenze zwischen Anker und cmdk-Auswahl.
 * Nach einem normalen Klick schließt die Palette; nach Cmd-Klick bleibt sie offen,
 * damit man mehrere Treffer in Tabs öffnen kann.
 */
function LinkCommandItem({
  value,
  to,
  onNavigate,
  children,
}: {
  value: string;
  to: string;
  onNavigate: () => void;
  children: React.ReactNode;
}) {
  const { go, openFromClick } = usePaletteNavigate();
  return (
    <CommandItem
      value={value}
      onSelect={() => {
        go(to);
        onNavigate();
      }}
      className="p-0"
    >
      <Link
        to={to}
        tabIndex={-1}
        onClick={(event) => {
          event.stopPropagation();
          if (!isPlainLeftClick(event)) return;
          openFromClick(event, to);
          onNavigate();
        }}
        className="flex min-w-0 flex-1 items-center gap-3 self-stretch px-2 py-2.5 text-inherit no-underline outline-none"
      >
        {children}
      </Link>
    </CommandItem>
  );
}

/** Debounce für die Eingabe — vermeidet eine Anfrage pro Tastenanschlag. */
function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Zentrierte Status-/Leerzustands-Fläche in der Trefferliste. */
function ListStatus({ children, tone }: { children: React.ReactNode; tone?: 'danger' }) {
  return (
    <div
      className={`flex min-h-[40vh] flex-col items-center justify-center gap-2 px-6 text-center text-sm ${
        tone === 'danger' ? 'text-destructive' : 'text-muted-foreground'
      }`}
    >
      {children}
    </div>
  );
}

/** Gemeinsame Optik der Pillen unter dem Suchfeld. */
const CHIP_BASE =
  'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50';
const CHIP_IDLE = 'border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground';

/**
 * Enter/Leertaste auf einer fokussierten Pille schaltet die Pille — und darf
 * nicht zu cmdk durchsickern (Enter würde sonst den markierten Treffer öffnen).
 */
function chipKeyDown(onSelect: () => void) {
  return (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onSelect();
    }
  };
}

/**
 * Kategorie-Chip (Variante B): Pill-Button unter dem Suchfeld, filtert die
 * Trefferliste client-seitig nach Ressourcentyp. Aktiv = Primärfarbe.
 */
function FilterChip({
  label,
  count,
  isActive,
  onSelect,
}: {
  label: string;
  count: number;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onSelect}
      onKeyDown={chipKeyDown(onSelect)}
      className={cn(
        CHIP_BASE,
        isActive ? 'border-transparent bg-primary text-primary-foreground' : CHIP_IDLE,
      )}
    >
      {label}
      <span className={cn('tabular-nums', isActive ? 'text-primary-foreground/70' : 'text-muted-foreground/70')}>
        {count}
      </span>
    </button>
  );
}

/**
 * „+N weitere" — klappt die restlichen Kategorie-Chips auf. Nötig, seit die
 * Suche rund 30 Typen kennt: sonst wächst die Chip-Leiste über den halben
 * Dialog und schiebt die Treffer aus dem Bild.
 */
function MoreChipsButton({ count, onSelect }: { count: number; onSelect: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      onKeyDown={chipKeyDown(onSelect)}
      className={cn(CHIP_BASE, CHIP_IDLE)}
    >
      +{count} weitere
    </button>
  );
}

/** Kennzeichnung eines nur ähnlich geschriebenen Treffers in der Trefferliste. */
function SimilarBadge({ hit }: { hit: SearchHit }) {
  const percent = formatSimilarity(hit);
  return (
    <Badge
      tone="outline"
      title={
        percent
          ? `Ähnlichkeit ${percent} — andere Schreibweise oder Tippfehler`
          : 'Ähnlich geschrieben — andere Schreibweise oder Tippfehler'
      }
    >
      ähnlich{percent ? ` · ${percent}` : ''}
    </Badge>
  );
}

/**
 * Grundgerüst der rechten Vorschau-Spalte (Variante C): Eyebrow, Icon-Kachel,
 * Inhalt, Aktion. Die Aktion ist ein echter Anker auf `route` (Knopf-Optik über
 * `Button asChild`, CLAUDE.md §9) — nach normalem Klick schließt die Palette.
 */
function PreviewFrame({
  eyebrow,
  icon,
  children,
  actionLabel,
  onNavigate,
  route,
}: {
  eyebrow: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  actionLabel: string;
  onNavigate: () => void;
  route: string;
}) {
  const { openFromClick } = usePaletteNavigate();
  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto p-6">
      <span className="text-[0.6875rem] font-medium uppercase tracking-wider text-muted-foreground">{eyebrow}</span>
      <span className="mt-4 flex size-14 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        {icon}
      </span>
      {children}
      <div className="mt-auto pt-6">
        <Button asChild variant="primary" className="w-full">
          <Link
            to={route}
            onClick={(event) => {
              if (!isPlainLeftClick(event)) return;
              openFromClick(event, route);
              onNavigate();
            }}
          >
            {actionLabel} <span aria-hidden>↵</span>
          </Link>
        </Button>
        {/* Ziel-Route als dezenter Hinweis unter der Aktion. */}
        <p className="mt-2 truncate text-center text-xs text-muted-foreground" title={route}>
          {route}
        </p>
      </div>
    </div>
  );
}

/** Vorschau der „Alle Ergebnisse"-Aktion. */
function AllResultsPreview({ query, onNavigate }: { query: string; onNavigate: () => void }) {
  return (
    <PreviewFrame
      eyebrow="Suche"
      icon={<ArrowRight className="size-7" strokeWidth={1.5} aria-hidden />}
      actionLabel="Öffnen"
      onNavigate={onNavigate}
      route={allResultsRoute(query)}
    >
      <span className="mt-4 break-words text-lg font-semibold text-foreground">
        Alle Ergebnisse für „{query}"
      </span>
      <span className="mt-1 text-sm text-muted-foreground">
        Öffnet die Ergebnisseite mit allen Treffern aller Module.
      </span>
    </PreviewFrame>
  );
}

/** Vorschau einer „neu anlegen"-Zeile: Hier wird noch nichts gespeichert. */
function CreatePreview({ row, onNavigate }: { row: CreateRow; onNavigate: () => void }) {
  const Icon = row.icon;
  return (
    <PreviewFrame
      eyebrow="Neu anlegen"
      icon={<Icon className="size-7" strokeWidth={1.5} aria-hidden />}
      actionLabel="Formular öffnen"
      onNavigate={onNavigate}
      route={row.to}
    >
      <span className="mt-4 break-words text-lg font-semibold text-foreground">{row.label}</span>
      <span className="mt-1 text-sm text-muted-foreground">
        Öffnet das Formular mit dem Suchtext als Name. Gespeichert wird erst dort.
      </span>
    </PreviewFrame>
  );
}

/** Ziel der Ergebnisseite für einen Suchbegriff. */
function allResultsRoute(query: string): string {
  return `/suche?q=${encodeURIComponent(query)}`;
}

/** Platzhalter der Vorschau-Spalte, solange kein Treffer markiert ist. */
function PreviewPlaceholder() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
      <span className="flex size-12 items-center justify-center rounded-full bg-muted">
        <MousePointerClick className="size-5" strokeWidth={1.5} aria-hidden />
      </span>
      Treffer auswählen für Details
    </div>
  );
}

/**
 * Universelle Suche als Command-Palette (ADR 0015) auf cmdk-Basis. ⌘K/Strg+K
 * öffnet sie (in der App-Shell verdrahtet); Pfeiltasten navigieren, Enter
 * springt zum Datensatz, Esc schließt. Das Filtern übernimmt der Server
 * (`shouldFilter={false}`). Großes Overlay (~2/3 des Fensters), Lade-/Fehler-/
 * Leerzustände sind explizit.
 *
 * Layout (bestätigtes Design, Mix aus Variante B + C):
 * - Kategorie-Chips unter dem Suchfeld filtern die Gruppen client-seitig.
 * - Ab `md`: geteiltes Layout — links Trefferliste, rechts Detailvorschau des
 *   markierten Eintrags (cmdk-Selektion kontrolliert via `value`/`onValueChange`).
 *
 * Reihenfolge der Liste (Tech-Lead 2026-08-12, „smarte Suche"):
 * 1. genaue Treffer, nach Typ gruppiert (Gruppen mit Kennungs-Treffer zuerst),
 * 2. Zeile „… weitere Kategorien anzeigen", wenn mehr als `VISIBLE_GROUP_LIMIT`
 *    Gruppen anstehen,
 * 3. Abschnitt „Ähnliche Treffer" — flach, nach Ähnlichkeit sortiert, jede Zeile
 *    mit „ähnlich"-Kennzeichen,
 * 4. „Alle Ergebnisse anzeigen" (Ergebnisseite).
 */
/**
 * Präfix, das die Palette vom Suchen aufs Befehle-Filtern umschaltet
 * (Tech-Lead 2026-08-04: `#` statt des früheren `>`). Bewusst EINE Konstante —
 * das Zeichen stand vorher an vier Stellen (Logik, Platzhalter, Fußzeile,
 * Kürzel-Übersicht) und lief so leicht auseinander.
 */
export const COMMAND_PREFIX = '#';

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 200);
  // Aktiver Kategorie-Filter ('all' = kein Filter) + kontrollierte cmdk-Markierung.
  const [activeType, setActiveType] = useState<SearchResourceTypeUi | 'all'>('all');
  const [selectedValue, setSelectedValue] = useState('');
  // Aufgeklappte Abschnitte (siehe VISIBLE_*_LIMIT).
  const [areAllChipsShown, setAreAllChipsShown] = useState(false);
  const [areAllGroupsShown, setAreAllGroupsShown] = useState(false);
  const [areAllSimilarShown, setAreAllSimilarShown] = useState(false);
  // Markierte Aktion in der Antwortkarte (Vorschlag C); `null` = Tastatur in der Trefferliste.
  const [actionIndex, setActionIndex] = useState<number | null>(null);
  const keyPlatform = useKeyPlatform();
  const pushToast = useToasts((state) => state.push);
  const { openInBackground } = usePaletteNavigate();

  // Befehls-Modus: leere Eingabe oder führendes `COMMAND_PREFIX` → Befehle statt Suche.
  const trimmedQuery = query.trim();
  const commandMode = trimmedQuery.length === 0 || trimmedQuery.startsWith(COMMAND_PREFIX);
  const commandFilter = trimmedQuery.startsWith(COMMAND_PREFIX)
    ? trimmedQuery.slice(COMMAND_PREFIX.length).trim().toLowerCase()
    : '';

  // Im Befehls-Modus keine Suchanfrage stellen.
  const { data, isLoading, isError } = useSearch(open && !commandMode ? debounced : '');

  // Befehle: Sprünge (aus der berechtigungsgefilterten Navigation) + Ansicht-Umschalter.
  const navItems = useNavItems();
  const { can } = usePermissions();
  const theme = useTheme((state) => state.theme);
  const toggleTheme = useTheme((state) => state.toggle);
  const { toggleSidebar } = useSidebar();
  const setShortcutsOpen = useUiStore((state) => state.setShortcutsOpen);
  const opensNewTab = useUiStore((state) => state.searchOpensNewTab);

  const commands = useMemo<PaletteCommand[]>(() => {
    const list: PaletteCommand[] = [];
    for (const action of QUICK_ACTIONS) {
      if (!can(action.permission)) continue;
      list.push({ id: action.id, label: action.label, keywords: action.keywords, hint: 'Anlegen', icon: action.icon, group: 'Aktionen', to: action.route });
    }
    for (const item of navItems) {
      list.push({ id: `nav:${item.to}`, label: item.label, hint: item.group, icon: item.icon, group: 'Navigation', to: item.to });
      for (const child of item.children ?? []) {
        list.push({
          id: `nav:${child.to}:${child.label}`,
          label: `${item.label} › ${child.label}`,
          keywords: `${item.label} ${child.label}`.toLowerCase(),
          hint: item.group,
          icon: item.icon,
          group: 'Navigation',
          to: child.to,
        });
      }
    }
    list.push({ id: 'view:theme', label: theme === 'dark' ? 'Helles Design' : 'Dunkles Design', keywords: 'theme design hell dunkel erscheinungsbild dark light', icon: theme === 'dark' ? Sun : Moon, group: 'Ansicht', run: toggleTheme });
    list.push({ id: 'view:sidebar', label: 'Seitenleiste ein-/ausklappen', keywords: 'sidebar seitenleiste menu navigation', icon: PanelLeft, group: 'Ansicht', run: toggleSidebar });
    list.push({ id: 'view:shortcuts', label: 'Tastenkürzel anzeigen', keywords: 'tastenkuerzel shortcuts hotkeys tastatur hilfe', icon: Keyboard, group: 'Ansicht', run: () => setShortcutsOpen(true) });
    return list;
  }, [navItems, can, theme, toggleTheme, toggleSidebar, setShortcutsOpen]);

  const filteredCommands = useMemo(() => {
    if (!commandFilter) return commands;
    return commands.filter(
      (command) =>
        command.label.toLowerCase().includes(commandFilter) ||
        (command.keywords ?? '').includes(commandFilter),
    );
  }, [commands, commandFilter]);

  const commandGroups = useMemo(() => {
    const order: PaletteCommand['group'][] = ['Aktionen', 'Navigation', 'Ansicht'];
    return order
      .map((group) => ({ group, items: filteredCommands.filter((command) => command.group === group) }))
      .filter((section) => section.items.length > 0);
  }, [filteredCommands]);
  const groups = data?.data.groups ?? [];
  const flatHits = useMemo(() => groups.flatMap((group) => group.hits), [groups]);

  // Genaue Treffer bleiben gruppiert, ähnliche kommen flach in einen eigenen
  // Abschnitt UNTER die genauen (Tech-Lead 2026-08-12).
  const { exactGroups, similarHits } = useMemo(() => splitSearchGroups(groups), [groups]);

  // Kategorie-Chips: je Typ eine Pille mit ALLEN Treffern des Typs (genau +
  // ähnlich), in der Reihenfolge der Trefferliste.
  const chips = useMemo(() => {
    const byType = new Map<SearchResourceTypeUi, { label: string; count: number }>();
    for (const group of exactGroups) {
      byType.set(group.type, { label: group.label || RESOURCE_LABEL[group.type], count: group.hits.length });
    }
    for (const { hit, label } of similarHits) {
      const entry = byType.get(hit.type);
      if (entry) entry.count += 1;
      else byType.set(hit.type, { label, count: 1 });
    }
    return [...byType].map(([type, entry]) => ({ type, ...entry }));
  }, [exactGroups, similarHits]);

  // Chip-Filter (client-seitig, der Server liefert alle Typen).
  const filteredGroups = useMemo(
    () => (activeType === 'all' ? exactGroups : exactGroups.filter((group) => group.type === activeType)),
    [exactGroups, activeType],
  );
  const filteredSimilar = useMemo(
    () => (activeType === 'all' ? similarHits : similarHits.filter((entry) => entry.hit.type === activeType)),
    [similarHits, activeType],
  );

  // Angezeigter Ausschnitt + was dahinter noch liegt.
  const shownGroups = useMemo(
    () => (areAllGroupsShown ? filteredGroups : filteredGroups.slice(0, VISIBLE_GROUP_LIMIT)),
    [filteredGroups, areAllGroupsShown],
  );
  const shownSimilar = useMemo(
    () => (areAllSimilarShown ? filteredSimilar : filteredSimilar.slice(0, VISIBLE_SIMILAR_LIMIT)),
    [filteredSimilar, areAllSimilarShown],
  );
  const hiddenGroups = filteredGroups.slice(shownGroups.length);
  const hiddenSimilarCount = filteredSimilar.length - shownSimilar.length;
  const shownChips = areAllChipsShown ? chips : chips.slice(0, VISIBLE_CHIP_LIMIT);
  const hiddenChipCount = chips.length - shownChips.length;

  // Tatsächlich gerenderte Treffer — nur die kann cmdk markieren.
  const renderedHits = useMemo(
    () => [...shownGroups.flatMap((group) => group.hits), ...shownSimilar.map((entry) => entry.hit)],
    [shownGroups, shownSimilar],
  );
  const visibleHitCount = countHits(filteredGroups) + filteredSimilar.length;

  const hasQuery = debounced.trim().length >= 1;
  const hasResults = hasQuery && !isLoading && !isError && flatHits.length > 0;

  // Nichts gefunden → anlegen anbieten, vorbelegt mit dem Suchtext (nur mit Recht).
  const createRows = useMemo<CreateRow[]>(() => {
    const text = debounced.trim();
    if (text.length < CREATE_MIN_LENGTH || text.startsWith(COMMAND_PREFIX)) return [];
    const name = encodeURIComponent(text);
    const rows: CreateRow[] = [];
    if (can('crm.create')) {
      rows.push({
        value: CREATE_CUSTOMER_VALUE,
        label: `„${text}" als neuen Kunden anlegen`,
        hint: 'Name vorbelegt, Rest im Formular',
        icon: Users,
        to: `/crm?neu=1&name=${name}`,
      });
    }
    if (can('projects.create')) {
      rows.push({
        value: CREATE_PROJECT_VALUE,
        label: `Neues Projekt „${text}"`,
        hint: 'Name vorbelegt, Rest im Formular',
        icon: FolderKanban,
        to: `/projekte?neu=1&name=${name}`,
      });
    }
    return rows;
  }, [debounced, can]);
  const shownCreateRows = hasQuery && !isLoading && !isError && flatHits.length === 0 ? createRows : [];

  // Beim Öffnen mit leerer Eingabe, ohne Filter und ohne Markierung starten.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveType('all');
      setSelectedValue('');
      setActionIndex(null);
    }
  }, [open]);

  // Neue Markierung oder neuer Suchtext → zurück in die Trefferliste.
  useEffect(() => {
    setActionIndex(null);
  }, [selectedValue, query]);

  // Chips und aufgeklappte Abschnitte zurücksetzen, sobald sich der (debouncte)
  // Suchbegriff ändert — sonst gilt der Aufklapp-Zustand einer alten Trefferliste.
  useEffect(() => {
    setActiveType('all');
    setAreAllChipsShown(false);
    setAreAllGroupsShown(false);
    setAreAllSimilarShown(false);
  }, [debounced]);

  // Markierung mit den gerenderten Treffern synchron halten: verschwindet der
  // markierte Eintrag (neue Suche, Chip-Wechsel), wird der erste Treffer markiert.
  useEffect(() => {
    if (commandMode) return; // im Befehls-Modus verwaltet cmdk die Markierung selbst
    const isSpecialRowVisible =
      (selectedValue === ALL_RESULTS_VALUE && hasResults) ||
      (selectedValue === MORE_GROUPS_VALUE && hiddenGroups.length > 0) ||
      (selectedValue === MORE_SIMILAR_VALUE && hiddenSimilarCount > 0) ||
      shownCreateRows.some((row) => row.value === selectedValue);
    const isSelectionVisible =
      isSpecialRowVisible || renderedHits.some((hit) => hitValue(hit) === selectedValue);
    if (!isSelectionVisible) {
      setSelectedValue(renderedHits[0] ? hitValue(renderedHits[0]) : (shownCreateRows[0]?.value ?? ''));
    }
  }, [renderedHits, selectedValue, hasResults, commandMode, hiddenGroups.length, hiddenSimilarCount, shownCreateRows]);

  // Markierter Treffer für die Vorschau-Spalte (nur gerenderte Treffer zählen).
  const selectedHit = useMemo(
    () => renderedHits.find((hit) => hitValue(hit) === selectedValue) ?? null,
    [renderedHits, selectedValue],
  );
  const isAllResultsSelected = selectedValue === ALL_RESULTS_VALUE && hasResults;
  const selectedCreateRow = shownCreateRows.find((row) => row.value === selectedValue) ?? null;

  // Antwort + Aktionen zum markierten Treffer (Vorschlag C).
  const answerState = useHitAnswer(commandMode ? null : selectedHit);
  const actions = answerState.answer?.actions ?? [];
  const activeAction = actionIndex != null ? actions[actionIndex] : undefined;

  const copyToClipboard = useCallback(
    async (copy: CopyTarget) => {
      try {
        await navigator.clipboard.writeText(copy.value);
        pushToast('success', `${copy.what} kopiert: ${copy.value}`);
      } catch {
        // Ohne Berechtigung/HTTPS verweigert der Browser den Zwischenspeicher.
        pushToast('danger', `${copy.what} ließ sich nicht kopieren — der Browser hat die Zwischenablage gesperrt.`);
      }
    },
    [pushToast],
  );

  /** ⌘/Strg+↵: als Tab im Hintergrund öffnen, die Suche bleibt offen (ohne Tab-Leiste: normal öffnen). */
  const openSelectedInBackground = useCallback(
    (to: string, title: string) => {
      if (!openInBackground(to)) {
        onClose();
        return;
      }
      // Bis die Seite selbst ihren Titel meldet, heißt der Tab wie der Treffer —
      // sonst stünde dort nur der Bereich („Projekte").
      useOpenTabs.getState().setLabel(to, 'heading', title);
      pushToast('info', `Im Hintergrund geöffnet: ${title}`);
    },
    [openInBackground, pushToast, onClose],
  );

  /**
   * Tastatur der Antwortkarte, vor cmdk (`preventDefault` hält cmdk heraus):
   * → (Schreibmarke am Ende) oder Tab springt in die Aktionen, ↑↓ wählt, ← bzw.
   * Umschalt+Tab zurück, ↵ führt aus (Klick auf das Element — Links bleiben Links).
   * Esc fängt `onEscapeKeyDown` ab, weil Radix es vor diesem Handler sieht.
   * Überall: ⌘/Strg+↵ öffnet im Hintergrund, ⌘/Strg+C kopiert die Nummer — aber
   * nur, wenn im Suchfeld nichts markiert ist (sonst kopiert man den Suchtext).
   */
  function handleKeyDown(event: React.KeyboardEvent) {
    if (commandMode || event.nativeEvent.isComposing) return;
    const isMod = event.metaKey || event.ctrlKey;
    const input = event.target instanceof HTMLInputElement ? event.target : null;

    if (actionIndex != null) {
      const last = actions.length - 1;
      if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
        event.preventDefault();
        setActionIndex(Math.min(last, actionIndex + 1));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActionIndex(Math.max(0, actionIndex - 1));
        return;
      }
      if (event.key === 'ArrowLeft' || (event.key === 'Tab' && event.shiftKey)) {
        event.preventDefault();
        setActionIndex(null);
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        return;
      }
      if (event.key === 'Enter' && !isMod) {
        event.preventDefault();
        document.getElementById(actionElementId(actionIndex))?.click();
        return;
      }
    }

    const selectedRoute = selectedHit
      ? featureRoute(selectedHit.type, selectedHit.id)
      : isAllResultsSelected
        ? allResultsRoute(debounced.trim())
        : null;
    if (event.key === 'Enter' && isMod && selectedRoute) {
      event.preventDefault();
      openSelectedInBackground(selectedRoute, selectedHit ? selectedHit.title : `Suche „${debounced.trim()}"`);
      return;
    }
    if (!selectedHit) return;
    const copy = answerState.answer?.copy;
    if (isMod && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'c' && copy) {
      const hasInputSelection = input != null && input.selectionStart !== input.selectionEnd;
      const hasPageSelection = (window.getSelection()?.toString() ?? '') !== '';
      if (hasInputSelection || hasPageSelection) return;
      event.preventDefault();
      void copyToClipboard(copy);
      return;
    }
    const isCaretAtEnd =
      input != null && input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
    const wantsActions = (event.key === 'ArrowRight' && isCaretAtEnd) || (event.key === 'Tab' && !event.shiftKey);
    if (wantsActions && actionIndex == null && actions.length > 0 && !window.matchMedia(NARROW_QUERY).matches) {
      event.preventDefault();
      setActionIndex(0);
    }
  }


  /** Chip-Klick: Kategorie umschalten und die Markierung auf den Erst-Treffer ziehen. */
  function filterByType(type: SearchResourceTypeUi | 'all') {
    setActiveType(type);
    const nextGroups = type === 'all' ? exactGroups : exactGroups.filter((group) => group.type === type);
    const nextSimilar =
      type === 'all' ? similarHits : similarHits.filter((entry) => entry.hit.type === type);
    const firstHit = nextGroups[0]?.hits[0] ?? nextSimilar[0]?.hit;
    setSelectedValue(firstHit ? hitValue(firstHit) : '');
  }

  /** „Mehr Kategorien": aufklappen und auf den ersten neu sichtbaren Treffer springen. */
  function showAllGroups() {
    setAreAllGroupsShown(true);
    const firstHidden = hiddenGroups[0]?.hits[0];
    if (firstHidden) setSelectedValue(hitValue(firstHidden));
  }

  /** „Mehr ähnliche Treffer": aufklappen und auf den ersten neu sichtbaren springen. */
  function showAllSimilar() {
    setAreAllSimilarShown(true);
    const firstHidden = filteredSimilar[shownSimilar.length]?.hit;
    if (firstHidden) setSelectedValue(hitValue(firstHidden));
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
      // In der Aktionsliste führt Esc zurück in die Trefferliste, statt zu schließen.
      onEscapeKeyDown={(event) => {
        if (actionIndex == null) return;
        event.preventDefault();
        setActionIndex(null);
      }}
      title="Universelle Suche"
      description="Projekte, Kunden, Artikel und Belege modulübergreifend durchsuchen"
      // Großes, zentrales Overlay: ~2/3 der Fensterbreite, hoch, füllende Trefferliste.
      className="top-[8vh] h-[80vh] max-h-[80vh] w-[94vw] max-w-[94vw] flex-col sm:max-w-[66vw]"
    >
      {/* Server filtert (ADR 0015) — cmdk-Filter deaktiviert. Markierung kontrolliert,
          damit die Vorschau-Spalte dem markierten Eintrag folgt. */}
      <Command
        shouldFilter={false}
        value={selectedValue}
        onValueChange={setSelectedValue}
        onKeyDown={handleKeyDown}
        className="flex min-h-0 flex-1 flex-col"
      >
        <CommandInput
          value={query}
          onValueChange={setQuery}
          placeholder={
            opensNewTab ? 'Neuer Tab: Projekt, Kunde, Beleg oder Seite suchen …' : `Suchen … oder mit ${COMMAND_PREFIX} Befehle aufrufen`
          }
          aria-label="Suchbegriff"
          className="h-12 text-base"
        />

        {/* Kategorie-Chips (Variante B): „Alle" + eine Pille je Ergebnis-Gruppe. */}
        {!commandMode && hasResults && (
          <div
            role="group"
            aria-label="Ergebnisse nach Kategorie filtern"
            className="flex flex-wrap items-center gap-1.5 border-b border-border px-3 py-2"
          >
            <FilterChip
              label="Alle"
              count={flatHits.length}
              isActive={activeType === 'all'}
              onSelect={() => filterByType('all')}
            />
            {shownChips.map((chip) => (
              <FilterChip
                key={chip.type}
                label={chip.label}
                count={chip.count}
                isActive={activeType === chip.type}
                onSelect={() => filterByType(chip.type)}
              />
            ))}
            {hiddenChipCount > 0 && (
              <MoreChipsButton count={hiddenChipCount} onSelect={() => setAreAllChipsShown(true)} />
            )}
          </div>
        )}

        {commandMode ? (
          <CommandList className="max-h-full min-h-0 flex-1 overflow-y-auto">
            {filteredCommands.length === 0 && (
              <ListStatus>Kein Befehl für „{commandFilter}".</ListStatus>
            )}
            {commandGroups.map(({ group, items }) => (
              <CommandGroup key={group} heading={group}>
                {items.map((command) => {
                  const Icon = command.icon;
                  const body = (
                    <>
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <Icon className="!size-4" strokeWidth={1.5} aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{command.label}</span>
                      {command.hint && (
                        <span className="shrink-0 text-xs text-muted-foreground">{command.hint}</span>
                      )}
                    </>
                  );
                  if (command.to !== undefined) {
                    return (
                      <LinkCommandItem key={command.id} value={command.id} to={command.to} onNavigate={onClose}>
                        {body}
                      </LinkCommandItem>
                    );
                  }
                  const run = command.run;
                  return (
                    <CommandItem
                      key={command.id}
                      value={command.id}
                      onSelect={() => {
                        run();
                        onClose();
                      }}
                      className="gap-3 py-2.5"
                    >
                      {body}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)] md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
          <CommandList className="max-h-full min-h-0 overflow-y-auto">
            {!hasQuery && (
              <ListStatus>
                <span className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Search className="size-5" strokeWidth={1.5} aria-hidden />
                </span>
                <span className="text-base font-medium text-foreground">Modulübergreifend suchen</span>
                <span>Projekte, Kunden, Artikel und Belege — einfach lostippen.</span>
              </ListStatus>
            )}
            {hasQuery && isLoading && (
              <ListStatus>
                <Spinner aria-hidden />
                Suche läuft …
              </ListStatus>
            )}
            {hasQuery && isError && (
              <ListStatus tone="danger">Suche fehlgeschlagen. Bitte erneut versuchen.</ListStatus>
            )}
            {hasQuery && !isLoading && !isError && flatHits.length === 0 && shownCreateRows.length === 0 && (
              <ListStatus>Keine Treffer für „{debounced}".</ListStatus>
            )}
            {shownCreateRows.length > 0 && (
              <>
                <p className="px-3 pt-4 pb-1 text-sm text-muted-foreground">Keine Treffer für „{debounced.trim()}".</p>
                <CommandGroup heading="Nichts gefunden — neu anlegen?">
                  {shownCreateRows.map((row) => {
                    const Icon = row.icon;
                    return (
                      <LinkCommandItem key={row.value} value={row.value} to={row.to} onNavigate={onClose}>
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-600">
                          <Plus className="!size-4" strokeWidth={1.5} aria-hidden />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm text-foreground">{row.label}</span>
                          <span className="truncate text-xs text-muted-foreground">{row.hint}</span>
                        </span>
                        <Icon className="!size-4 shrink-0 text-muted-foreground" strokeWidth={1.5} aria-hidden />
                      </LinkCommandItem>
                    );
                  })}
                </CommandGroup>
              </>
            )}

            {shownGroups.map((group) => {
              const Icon = RESOURCE_ICON[group.type];
              return (
                <CommandGroup key={group.type} heading={group.label}>
                  {group.hits.map((hit) => (
                    <LinkCommandItem
                      key={hitValue(hit)}
                      value={hitValue(hit)}
                      to={featureRoute(hit.type, hit.id)}
                      onNavigate={onClose}
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <Icon className="!size-4" strokeWidth={1.5} aria-hidden />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm text-foreground">{hit.title}</span>
                        {hit.subtitle && (
                          <span className="truncate text-xs text-muted-foreground">{hit.subtitle}</span>
                        )}
                      </span>
                      {hit.badge && <Badge>{searchBadgeLabel(hit)}</Badge>}
                    </LinkCommandItem>
                  ))}
                </CommandGroup>
              );
            })}

            {/* Weggelassene Kategorien — eine Zeile statt 20 Überschriften. */}
            {hiddenGroups.length > 0 && (
              <CommandGroup>
                <CommandItem value={MORE_GROUPS_VALUE} onSelect={showAllGroups} className="gap-3 py-2.5">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                    <ChevronDown className="!size-4" strokeWidth={1.5} aria-hidden />
                  </span>
                  <span className="flex-1 text-sm text-foreground">
                    {plural(hiddenGroups.length, 'weitere Kategorie', 'weitere Kategorien')} mit{' '}
                    {plural(countHits(hiddenGroups), 'Treffer', 'Treffern')} anzeigen
                  </span>
                </CommandItem>
              </CommandGroup>
            )}

            {/* Ähnliche Treffer: eigener Abschnitt UNTER den genauen Treffern. */}
            {shownSimilar.length > 0 && (
              <>
                <CommandSeparator className="mx-2 my-1" />
                {/* Überschrift als cmdk-`heading`, damit die Gruppe sie auch
                    vorgelesen bekommt (aria-labelledby) — nicht als loser Text. */}
                <CommandGroup
                  heading={
                    <span className="flex flex-col gap-0.5 pt-1">
                      <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                        <Sparkles className="size-3.5 text-muted-foreground" strokeWidth={1.5} aria-hidden />
                        Ähnliche Treffer
                      </span>
                      <span className="text-xs font-normal text-muted-foreground">
                        {countHits(exactGroups) > 0
                          ? 'Nicht genau dein Suchbegriff — ähnlich geschrieben (Tippfehler oder andere Schreibweise).'
                          : `Nichts stimmt genau mit „${debounced.trim()}" überein. Diese Datensätze sind ähnlich geschrieben.`}
                      </span>
                    </span>
                  }
                >
                  {shownSimilar.map(({ hit, label }) => {
                    const Icon = RESOURCE_ICON[hit.type];
                    return (
                      <LinkCommandItem
                        key={hitValue(hit)}
                        value={hitValue(hit)}
                        to={featureRoute(hit.type, hit.id)}
                        onNavigate={onClose}
                      >
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <Icon className="!size-4" strokeWidth={1.5} aria-hidden />
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm text-foreground">{hit.title}</span>
                          <span className="truncate text-xs text-muted-foreground">
                            {[label, hit.subtitle].filter(Boolean).join(' · ')}
                          </span>
                        </span>
                        <SimilarBadge hit={hit} />
                      </LinkCommandItem>
                    );
                  })}
                  {hiddenSimilarCount > 0 && (
                    <CommandItem value={MORE_SIMILAR_VALUE} onSelect={showAllSimilar} className="gap-3 py-2.5">
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        <ChevronDown className="!size-4" strokeWidth={1.5} aria-hidden />
                      </span>
                      <span className="flex-1 text-sm text-foreground">
                        {plural(hiddenSimilarCount, 'weiteren ähnlichen Treffer', 'weitere ähnliche Treffer')}{' '}
                        anzeigen
                      </span>
                    </CommandItem>
                  )}
                </CommandGroup>
              </>
            )}

            {hasResults && (
              <CommandGroup>
                <LinkCommandItem value={ALL_RESULTS_VALUE} to={allResultsRoute(debounced.trim())} onNavigate={onClose}>
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent-50 text-accent-600">
                    <ArrowRight className="!size-4" strokeWidth={1.5} aria-hidden />
                  </span>
                  <span className="flex-1 text-sm text-foreground">
                    Alle Ergebnisse für „{debounced.trim()}" anzeigen
                  </span>
                </LinkCommandItem>
              </CommandGroup>
            )}
          </CommandList>

          {/* Vorschau-Spalte: nur ab `md` sichtbar, darunter einspaltig. */}
          <aside aria-label="Detailvorschau" className="hidden min-h-0 border-l border-border md:block">
            {selectedHit ? (
              <HitAnswerCard
                hit={selectedHit}
                groupLabel={
                  chips.find((chip) => chip.type === selectedHit.type)?.label ??
                  RESOURCE_LABEL[selectedHit.type]
                }
                state={answerState}
                activeIndex={actionIndex}
                keyPlatform={keyPlatform}
                onNavigate={onClose}
                onBackground={(to) => openSelectedInBackground(to, selectedHit.title)}
                onCopy={(copy) => void copyToClipboard(copy)}
              />
            ) : isAllResultsSelected ? (
              <AllResultsPreview query={debounced.trim()} onNavigate={onClose} />
            ) : selectedCreateRow ? (
              <CreatePreview row={selectedCreateRow} onNavigate={onClose} />
            ) : (
              <PreviewPlaceholder />
            )}
          </aside>
        </div>
        )}

        {/* Fuß: Tastatur-Hinweise + Trefferzahl. */}
        <div className="flex items-center justify-between gap-4 border-t border-border px-3 py-2 text-xs text-muted-foreground">
          {activeAction ? (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> Aktion wählen</span>
              <span className="flex items-center gap-1"><Kbd>{keyLabel('enter', keyPlatform)}</Kbd> ausführen</span>
              <span className="flex items-center gap-1"><Kbd>←</Kbd> oder <Kbd>{keyLabel('esc', keyPlatform)}</Kbd> zurück</span>
              {/* Ansage für Screenreader: Der Fokus bleibt im Suchfeld, die Aktion wechselt. */}
              <span className="sr-only" aria-live="polite">
                Aktion {activeAction.label}, {(actionIndex ?? 0) + 1} von {actions.length}
              </span>
            </span>
          ) : (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> navigieren</span>
              <span className="flex items-center gap-1"><Kbd>{keyLabel('enter', keyPlatform)}</Kbd> öffnen</span>
              {/* Die Zusatz-Kürzel nur, wo Platz ist — die Fußzeile soll einzeilig bleiben. */}
              {selectedHit && (
                <>
                  <span className="hidden items-center gap-1 md:flex"><Kbd>→</Kbd> Aktionen</span>
                  <span className="hidden items-center gap-1 lg:flex">
                    <Kbd>{formatShortcut(SHORTCUT_KEYS.paletteBackgroundTab, keyPlatform)}</Kbd> Hintergrund-Tab
                  </span>
                  {answerState.answer?.copy && (
                    <span className="hidden items-center gap-1 2xl:flex">
                      <Kbd>{formatShortcut(SHORTCUT_KEYS.copy, keyPlatform)}</Kbd> {answerState.answer.copy.what} kopieren
                    </span>
                  )}
                </>
              )}
              <span className="flex items-center gap-1"><Kbd>{COMMAND_PREFIX}</Kbd> Befehle</span>
              <span className="flex items-center gap-1"><Kbd>{keyLabel('esc', keyPlatform)}</Kbd> schließen</span>
            </span>
          )}
          {hasResults && (
            <span className="shrink-0 whitespace-nowrap tabular-nums">
              {activeType === 'all'
                ? `${flatHits.length} Treffer`
                : `${visibleHitCount} von ${flatHits.length} Treffern`}
              {similarHits.length > 0 && ` · ${similarHits.length} ähnlich`}
            </span>
          )}
        </div>
      </Command>
    </CommandDialog>
  );
}
